import { useCallback, useEffect, useState } from 'react';
import { History, X, Loader2, Check, Clock } from 'lucide-react';

// SHARED, VERTICAL-AGNOSTIC "Recent actions" panel. Used by hotel staff,
// automotive salespeople, property residents — any surface that can call the
// generic /api/action-history endpoints. Removing an item here is a VIEW
// PREFERENCE: it never deletes the underlying record, and a manager's timeline
// is unaffected.
const fmt = (iso) => {
    try {
        const d = new Date(iso);
        const today = new Date().toDateString() === d.toDateString();
        const time = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
        return today ? time : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`;
    } catch { return iso; }
};

export default function ActionHistoryPanel({ api, labels, testid = 'action-history' }) {
    const L = labels || {};
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const load = useCallback(async () => {
        try { setItems((await api.list())?.items || []); } catch { /* keep last */ }
        finally { setLoaded(true); }
    }, [api]);

    useEffect(() => { load(); }, [load]);

    const remove = async (id) => {
        setBusy(true);
        try { setItems((await api.dismiss([id]))?.items || []); } finally { setBusy(false); }
    };
    const clearAll = async () => {
        setBusy(true);
        try { setItems((await api.clear())?.items || []); } finally { setBusy(false); }
    };

    if (!loaded) return null;

    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                <History className="w-4 h-4 text-sky-300" />
                <p className="text-sm font-semibold text-white flex-1">{L.title}</p>
                {items.length > 0 && (
                    <button type="button" onClick={clearAll} disabled={busy}
                        data-testid={`${testid}-clear`}
                        className="text-xs font-semibold text-slate-300 hover:text-white transition-colors disabled:opacity-40">
                        {L.clearAll}
                    </button>
                )}
            </div>
            {items.length === 0 ? (
                <p className="text-xs text-slate-400" data-testid={`${testid}-empty`}>{L.empty}</p>
            ) : (
                <ul className="space-y-1.5" data-testid={`${testid}-list`}>
                    {items.map((it) => (
                        <li key={it.event_id} data-testid={`${testid}-item-${it.event_id}`}
                            className="flex items-start gap-2 rounded-xl bg-black/20 px-2.5 py-2">
                            <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-white truncate">
                                    <span className="font-semibold">{L.action?.[it.action] || it.action}</span>
                                    {it.label ? ` — ${it.label}` : ''}
                                </p>
                                <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />{fmt(it.at)}
                                    {it.notes ? ` · ${it.notes}` : ''}
                                </p>
                            </div>
                            <button type="button" onClick={() => remove(it.event_id)} disabled={busy}
                                aria-label={L.remove} title={L.remove}
                                data-testid={`${testid}-remove-${it.event_id}`}
                                className="shrink-0 text-slate-400 hover:text-white transition-colors disabled:opacity-40">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <p className="text-[11px] text-slate-500 mt-2">{L.footnote}</p>
            {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-400 mt-1" />}
        </div>
    );
}

// Post-action bar: the action is ALREADY saved (keep is the default, so a busy
// person never loses their history by doing nothing). "Dismiss" only hides it
// from their own list.
export function ActionKeepBar({ labels, onDismiss, onKeep, testid = 'action-keep-bar' }) {
    const L = labels || {};
    return (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2"
            data-testid={testid}>
            <Check className="w-4 h-4 text-emerald-300 shrink-0" />
            <p className="text-xs text-emerald-100 flex-1">{L.savedToHistory}</p>
            <button type="button" onClick={onKeep} data-testid={`${testid}-keep`}
                className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-500 rounded-full px-2.5 py-1 transition-colors">
                {L.keep}
            </button>
            <button type="button" onClick={onDismiss} data-testid={`${testid}-dismiss`}
                className="text-xs font-semibold text-slate-200 border border-white/20 hover:border-white/40 rounded-full px-2.5 py-1 transition-colors">
                {L.dismiss}
            </button>
        </div>
    );
}
